import {useIsMutating, useQuery, UseQueryResult} from '@tanstack/react-query';
import React, {useCallback, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector, useStore} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import {toast} from 'react-toastify';
import i18n from 'i18next';
import {QUERY_KEYS} from '../../../../utils/queryKeys';
import {NOTIFICATIONS_HAMBURGER_SIZE_DEFAULT_VALUES} from '../../../../constants';
import Button from '../../../../incase-domain/components/button/Button';
import DropdownMenuCompact, {
    DropdownMenuCompactMenuPosition,
} from '../../../../incase-domain/components/dropdownMenuCompact/DropdownMenuCompact';
import ConfirmModal from '../../../../incase-domain/components/modals/confirmModal/ConfirmModal';
import {INotificationResponse, NotificationStatus} from '../../../../incase-domain/notification';
import {IPresentationTheme} from '../../../../incase-domain/presentationTheme';
import {logout} from '../../../../service/authorization';
import useNotification from '../../../../service/hooks/useNotification';
import {UPDATE_SLIDE_KEY} from '../../../../utils/hooks/slideUpdates/useSaveSlide';
import {useConversationsUnreadCount} from '../../../../service/hooks/useConversationsUnreadCount';
import {fetchPresentationThemeListService} from '../../../../service/presentationTheme.service';
import {resetPresentationReducerAction, setPresentationSettingsModalAction} from '../../../../store/actions/presentation';
import {selectedPresentationMetaSelector} from '../../../../store/selectors/presentation.selectors';
import {
    isSlideModifiedSelector,
    selectedSlideHiddenSelector,
    selectedSlideIdSelector,
    selectedSlideSelector,
} from '../../../../store/selectors/slide.selectors';
import {presUpdateSpinnerSelector} from '../../../../store/selectors/spinner.selector';
import {AppState} from '../../../../store/reducer';
import {
    buildAdminRequestParams,
    createPlayPresentationUrl,
    createShortPresentationUrl,
    openPlayerWindow,
} from '../../../../utils/presentation';
import {copyToClipboard} from '../../../../utils/copyToClipboard';
import {useCreateAutoSlug} from '../../../../service/hooks/useSlug';
import {useSlideUpdateContext} from '../../../context/SetSlideUpdateContext';
import CreatePresentationLinksModals from '../../presentationList/presentationLinks/CreatePresentationLinksModals';
import MenuBar from './menubar/MenuBar';
import PresentationNameInput from './PresentationNameInput';

function EditNavbarUserMenuDropdown({
    notifications,
    onRequestLogout,
}: {
    notifications: UseQueryResult<INotificationResponse>;
    onRequestLogout: () => void;
}): JSX.Element {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const {numberOfUnreadMessages} = useConversationsUnreadCount();

    const menuItems = useMemo(
        () => [
            [
                {
                    label: t('navigation.messages'),
                    icon: 'solar:letter-linear',
                    onClick: () => {
                        navigate('/messages');
                    },
                    badge: numberOfUnreadMessages,
                },
                {
                    label: t('navigation.notifications'),
                    icon: 'solar:bell-linear',
                    onClick: () => {
                        navigate('/notifications');
                    },
                    badge: notifications?.data?.numberOfUnseen || 0,
                },
                {
                    label: t('header.languages.languages'),
                    icon: 'solar:earth-linear',
                    items: [
                        [
                            {
                                label: t('header.languages.hr'),
                                icon: 'emojione:flag-for-croatia',
                                onClick: () => i18n.changeLanguage('hr'),
                                active: i18n.language === 'hr',
                            },
                            {
                                label: t('header.languages.en'),
                                icon: 'emojione:flag-for-united-kingdom',
                                onClick: () => i18n.changeLanguage('en'),
                                active: i18n.language === 'en',
                            },
                        ],
                    ],
                },
                {
                    label: t('header.profile.settings'),
                    icon: 'solar:settings-linear',
                    onClick: () => {
                        navigate('/settings');
                    },
                },
            ],
            [
                {
                    label: t('header.profile.logOut'),
                    icon: 'solar:logout-3-linear',
                    onClick: onRequestLogout,
                },
            ],
        ],
        [t, navigate, notifications?.data?.numberOfUnseen, numberOfUnreadMessages, onRequestLogout]
    );

    return (
        <DropdownMenuCompact items={menuItems} menuPosition={DropdownMenuCompactMenuPosition.LEFT}>
            <Button variant="ghost" leading="solar:hamburger-menu-linear" square />
        </DropdownMenuCompact>
    );
}

function EditNavbar(): JSX.Element {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const store = useStore<AppState>();
    const {t} = useTranslation();

    const {setAndSaveSlide} = useSlideUpdateContext();
    const isSlideUpdating = useIsMutating(UPDATE_SLIDE_KEY);
    const presUpdateSpinner = useSelector(presUpdateSpinnerSelector);
    const {notifications} = useNotification(NOTIFICATIONS_HAMBURGER_SIZE_DEFAULT_VALUES, [NotificationStatus.ACTIVE]);

    const slideId = useSelector(selectedSlideIdSelector);
    const slideHidden = useSelector(selectedSlideHiddenSelector);

    const selectedPresentationMeta = useSelector(selectedPresentationMetaSelector);
    const [createPresentationLinksModal, setCreatePresentationLinksModal] = useState(false);
    const [logoutModal, setLogoutModal] = useState(false);
    const {mutateAsync: createAutoSlug} = useCreateAutoSlug();

    const presentationThemeList: UseQueryResult<IPresentationTheme[]> = useQuery<IPresentationTheme[]>(QUERY_KEYS.theme.lists(), () =>
        fetchPresentationThemeListService()
    );

    const mutateOnModified = useCallback(
        (cb: () => void): void => {
            const state = store.getState();
            if (isSlideModifiedSelector(state)) {
                setAndSaveSlide(selectedSlideSelector(state), {onSuccess: cb});
            } else cb();
        },
        [store, setAndSaveSlide]
    );

    const specialScreens = ['report', 'gamification', 'settings', 'rules'];

    function isNestedPresentationScreen(): boolean {
        const routeSuffix = window.location.pathname.split('/').at(-1);
        return !!routeSuffix && specialScreens.includes(routeSuffix);
    }

    function getThemeName(): string {
        return presentationThemeList.data?.find((theme) => theme._id === selectedPresentationMeta.presentationThemeId)?.themeName || '';
    }

    function extractURLSuffix(): string {
        return window.location.pathname.split('/').at(-1) || '';
    }

    const handleCopyLinkToClipboard = async (): Promise<void> => {
        try {
            const slugDoc = await createAutoSlug({presentationId: selectedPresentationMeta._id});
            await copyToClipboard(createShortPresentationUrl(slugDoc.slug));
            toast.info(t('presEditPage.subNavBar.copiedToClipboard'));
        } catch (err) {
            toast.info(t('presEditPage.subNavBar.failedToCopy'));
        }
    };

    const onRequestLogout = useCallback(() => setLogoutModal(true), []);

    const closeCreatePresentationLinksModal = useCallback(() => setCreatePresentationLinksModal(false), []);

    return (
        <>
            <header className="c-headerEdit">
                <div className="c-headerEdit__left">
                    <Button
                        square
                        variant="ghost"
                        onClick={() => {
                            mutateOnModified(() => {
                                dispatch(resetPresentationReducerAction());

                                if (isNestedPresentationScreen()) {
                                    navigate(-1);
                                } else {
                                    navigate('/editor');
                                }
                            });
                        }}
                        className="c-nav__item"
                        leading="ph:caret-left-bold"
                    />
                    <div className="c-sidepanel__input c-headerEdit__presentationTitle">
                        <PresentationNameInput className="c-sidepanel__input_field" />
                    </div>
                </div>
                <div className="c-headerEdit__center">
                    {isNestedPresentationScreen() ? <div className="c-nav__title">{extractURLSuffix()}</div> : <MenuBar />}
                </div>

                <div className="c-headerEdit__right">
                    {(!!isSlideUpdating || presUpdateSpinner) && (
                        <Button size="sm" variant="ghost" color="frost" leading="svg-spinners:180-ring" disablePointerEvents>
                            {t('presEditPage.header.saving')}
                        </Button>
                    )}
                    <Button
                        color={getThemeName() ? 'primary' : 'frost'}
                        variant="ghost"
                        leading="solar:pallete-2-bold"
                        size="sm"
                        onClick={() => dispatch(setPresentationSettingsModalAction(true, 3))}
                    >
                        {getThemeName() || t('presEditPage.themeModal.noTheme')}
                    </Button>
                    <Button
                        className="c-headerEdit__button"
                        color="primary"
                        variant="outline"
                        leading="solar:share-bold"
                        size="sm"
                        onClick={() => {
                            selectedPresentationMeta.requestParams?.length
                                ? setCreatePresentationLinksModal(true)
                                : handleCopyLinkToClipboard();
                        }}
                    >
                        {t('editorPage.share')}
                    </Button>
                    <Button
                        className="c-headerEdit__button"
                        color="secondary"
                        leading="solar:play-bold"
                        size="sm"
                        onClick={() =>
                            openPlayerWindow(async () => {
                                const slugDoc = await createAutoSlug({
                                    presentationId: selectedPresentationMeta._id,
                                    params: buildAdminRequestParams(selectedPresentationMeta),
                                });
                                const shortUrl = createShortPresentationUrl(slugDoc.slug);
                                return slideId ? `${shortUrl}?slideId=${slideId}` : shortUrl;
                            }, createPlayPresentationUrl({presentation: selectedPresentationMeta, slideId}))
                        }
                        disabled={slideHidden}
                    >
                        {t('editorPage.play')}
                    </Button>
                    <EditNavbarUserMenuDropdown notifications={notifications} onRequestLogout={onRequestLogout} />
                </div>
            </header>
            <CreatePresentationLinksModals
                key={selectedPresentationMeta?._id}
                presentation={selectedPresentationMeta}
                open={createPresentationLinksModal}
                closeModal={closeCreatePresentationLinksModal}
            />
            <ConfirmModal
                message={t('sidebar.logoutText')}
                icon="solar:logout-3-bold"
                isOpen={logoutModal}
                onCancel={() => setLogoutModal(false)}
                onConfirm={() => logout()}
                confirmText={t('sidebar.logoutConfirm')}
                cancelText={t('sidebar.cancel')}
                showCancel
                showConfirm
                dataCy="logoutModal"
            />
        </>
    );
}

export default React.memo(EditNavbar);
